import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { faComments } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule, PageHeader, SelectBox,
} from '@domain-group/suit-theme'

import { useNumericParams } from '@domain-group/react-hooks'

import useCampaignSelector from '@hooks/useCampaignSelector'
import useConversation, { generateUrls } from '@hooks/useConversation'
import useConversations from '@hooks/useConversations'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import type { ConversationModel } from '@domain-group/realhub-backend-api-types'

import Conversation from '../Conversation'
import ConversationListItem from '../ConversationListItem'

const selectListItem = (campaignId: number, conversation: ConversationModel) => {
  const prefix = `campaigns/${campaignId}/edit`
  window.location.hash = `#/${prefix}/conversations/${conversation.id}`
}

const PageContent = () => {
  const { campaignId, conversationId } = useNumericParams()

  useLocation()

  const { campaign: { address } } = useCampaignSelector()

  const conversationsPayload = useConversations({
    filters: {
      campaign_id: campaignId,
    },
    performHttpRequests: !!campaignId,
    requestOptions: defaultRequestOptions.conversation,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredConversations,
    filteredConversationsCount,
    hasFilteredConversations,
    loading,
  } = conversationsPayload

  const { callbacks: { autoSelectConversation } } = useConversation()

  const { conversationsIndexUrl } = generateUrls({ conversationId, prefixRoute: `campaigns/${campaignId}/edit` })
  const selectedConversation = filteredConversations.find(conversation => conversation.id === conversationId)

  useEffect(
    // @ts-expect-error TS2345
    () => autoSelectConversation(campaignId, conversationId, filteredConversations, selectedConversation),
    [filteredConversations.length, conversationId],
  )

  const isInitialLoading = loading && !hasFilteredConversations

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Conversations</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="conversations"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={[
          {
            href: conversationsIndexUrl,
            icon: faComments,
            key: 'conversations',
            title: 'Conversations',
          },
        ]}
        title="Conversations"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.conversations.banner" />

            <LoadingModule loading={isInitialLoading} times={3} />

            {!isInitialLoading && !hasFilteredConversations
            && (
            <Conversation conversation={{
              campaign_id: campaignId,
              subject_id: campaignId,
              subject_type: 'Campaign',
            }}
            />
            )}

            {!isInitialLoading && hasFilteredConversations && (
              <DashboardModule
                boxProps={{ display: ['none', 'flex'] }}
                contentBoxProps={{ flexDirection: 'column' }}
                title="Conversations"
              >
                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection maxHeight={600}>
                  {filteredConversations.map(conversation => (
                    <ConversationListItem campaignId={campaignId} key={conversation.id} conversation={conversation} />
                  ))}
                </DashboardModule.ScrollableSection>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredConversationsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}

            {!loading && hasFilteredConversations && (
              <Box backgroundColor="white" display={['flex', 'none']} marginBottom="large">
                <SelectBox
                  callbacks={{ selectItem: (item: ConversationModel) => selectListItem(campaignId, item) }}
                  // @ts-expect-error TS2322
                  items={filteredConversations}
                  selectedItemKey={selectedConversation?.id}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.conversations.sidebar" />

            {!loading && hasFilteredConversations && (
              <Conversation
                // @ts-expect-error TS2322
                conversation={selectedConversation}
                key={selectedConversation?.id}
              />
            )}
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
